import { Injectable } from '@angular/core';

/** A request with less than this left will land after the token is gone. */
const EXPIRY_MARGIN_MS = 5000;

/**
 * A token nobody has used for this long is dropped from memory, whatever the server says.
 *
 * @ticket PS-6495 — the server hands out ~12 h tokens (see `setToken`), while the token is only
 * needed for the few requests of one setup run. A suspended run on a shared device must not keep a
 * bearer that can book against the phone service for the rest of the day. Frontend-only by
 * decision (2026-10-12); the server-side lifetime stays as it is.
 */
export const IDLE_TIMEOUT_MS = 30 * 60 * 1000;

/**
 * PS-6495 — custody of the access token the inline login returns.
 *
 * **D3: memory only.** No `localStorage`, no `sessionStorage`, no cookie, and no refresh token is
 * kept. The Connect page is reached from an emailed link, so a shared or family device is the
 * normal case rather than the edge case — anything persisted here outlives the customer's visit.
 * The `sessionStorage` stash used by the same-tab fallback holds the half-written task draft and
 * deliberately not the token. There is no refresh token either: an expired token sends the customer
 * back through the sign-in step, which is one screen, in the dialog, with their entries kept.
 *
 * The token itself is opaque (`OAuth2TokenFormat.REFERENCE` on the authorization server), so there
 * is nothing in it to read or cache — the only reason to keep it is to attach it to the two
 * allowlisted requests, and to know when it has run out.
 */
@Injectable({
	providedIn: 'root',
})
export class AuthTokenStore {
	private accessToken: string | null = null;
	private expiresAt: number | null = null;
	private idleTimer: ReturnType<typeof setTimeout> | null = null;
	private idledOut = false;

	/**
	 * The lifetime is optional because the caller may not have one, and an absent lifetime must never
	 * be read as "expired" — that would lock a perfectly good token out of its own flow.
	 *
	 * Why a lifetime matters here at all: the authorization server reuses a still-valid token for
	 * the same client and user, so what arrives can be minutes from expiry even though the client's
	 * nominal life is twelve hours. Measured on live 2026-09-10: `expires_in` 42069 against a fresh
	 * 43200.
	 */
	setToken(token: string, expiresInSeconds?: number): void {
		this.accessToken = token;
		this.expiresAt =
			typeof expiresInSeconds === 'number' ? Date.now() + expiresInSeconds * 1000 : null;
		this.idledOut = false;
		this.armIdleTimer();
	}

	/**
	 * Marks the token as used, which restarts the idle timeout. Called by `authTokenInterceptor`
	 * whenever it attaches the bearer — a request is the only activity the token itself sees.
	 */
	touch(): void {
		if (this.accessToken !== null) {
			this.armIdleTimer();
		}
	}

	/**
	 * A margin, not a deadline. A token with three seconds left is gone by the time a request
	 * reaches the server, and a predictable 401 mid-flight costs the customer a round trip and shows
	 * them a failure nobody had to see.
	 *
	 * This is a HINT and never the authority. Clocks drift, and the server can revoke a token early
	 * — `false` here does not promise the server agrees. The 401 is what decides; this only avoids
	 * sending a request that is already known to be pointless.
	 *
	 * A token dropped by the idle timeout also reads as expired, so the flow sends the customer
	 * through the sign-in step again (`requireNewSignIn`) instead of firing a request without one.
	 */
	get isExpired(): boolean {
		if (this.idledOut) {
			return true;
		}
		return this.expiresAt !== null && Date.now() >= this.expiresAt - EXPIRY_MARGIN_MS;
	}

	get token(): string | null {
		return this.accessToken;
	}

	get hasToken(): boolean {
		return this.accessToken !== null;
	}

	/**
	 * Drops the token. Called by the host when a run ends in any way — booked, dismissed or failed
	 * (`CallbackOnCallnote.endRun`) — and by `requireNewSignIn()` when the token turns out to be
	 * stale mid-run. Deliberately NOT called on suspend: that run continues.
	 */
	clear(): void {
		this.dropToken();
		this.idledOut = false;
	}

	private armIdleTimer(): void {
		this.cancelIdleTimer();
		this.idleTimer = setTimeout(() => {
			this.dropToken();
			this.idledOut = true;
		}, IDLE_TIMEOUT_MS);
	}

	private cancelIdleTimer(): void {
		if (this.idleTimer !== null) {
			clearTimeout(this.idleTimer);
			this.idleTimer = null;
		}
	}

	private dropToken(): void {
		this.cancelIdleTimer();
		this.accessToken = null;
		this.expiresAt = null;
	}
}
